// Two-factor authentication — the second step of the login card (2026-10-20).
//
// Rendered by pages/login.tsx in place of the forms once POST /login/user (or the magic-link
// poller) answers a TwoFactorChallenge. Three screens, one component:
//   verify        6-digit authenticator code (auto-submits on the sixth digit) or a backup code
//   enrol         QR + manual key from POST /login/2fa/enrol, confirmed with one code
//   backup-codes  the ten single-use codes, shown exactly once; Continue is gated on the checkbox
// Every call carries the challengeToken as Authorization: Bearer — there is no session yet, and
// apiFetch never overwrites a caller-provided Authorization header. The global 401 handler
// ignores /login* paths, so an expired challenge never force-logs-out the device.

import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Check, Copy, Download, KeyRound, ShieldCheck, Smartphone } from "lucide-react";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { storeTrustToken } from "../helpers/twoFactorTrust";
import type { TwoFactorChallenge, TwoFactorCompletion } from "../../types/Auth";
import "../../styles/two-factor.css";

type Screen = "verify" | "enrol" | "backup-codes";

interface EnrolData { secret: string; otpauth: string; label?: string }

interface Props {
    challenge: TwoFactorChallenge;
    /** The same handler the password login uses — receives the full login payload. */
    onComplete: (response: TwoFactorCompletion) => void;
    /** "Back to login": the page clears the challenge and the password field. */
    onBack: () => void;
    /** The 5-minute pending window ran out (countdown, or the BE said so). */
    onExpired: () => void;
}

const DIGITS = /^\d{6}$/;
const BACKUP = /^[A-Za-z0-9]{4}-?[A-Za-z0-9]{4}$/;

// The manual key in rows of four 4-letter groups ("JBSW Y3DP EHPK 3PXP" / ...): the same shape
// an authenticator app shows, and it never wraps mid-group on a phone.
const groupKey = (secret: string) => {
    const groups = secret.match(/.{1,4}/g) ?? [secret];
    const rows: string[] = [];
    for (let i = 0; i < groups.length; i += 4) rows.push(groups.slice(i, i + 4).join(" "));
    return rows.join("\n");
};

const TwoFactorStep = ({ challenge, onComplete, onBack, onExpired }: Props) => {
    const { t } = useTranslation();
    const [screen, setScreen] = useState<Screen>(challenge.enrolmentRequired ? "enrol" : "verify");
    const [otp, setOtp] = useState("");
    const [useBackup, setUseBackup] = useState(false);
    const [trustDevice, setTrustDevice] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [enrol, setEnrol] = useState<EnrolData | null>(null);
    const [enrolError, setEnrolError] = useState<string | null>(null);
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [finalPayload, setFinalPayload] = useState<TwoFactorCompletion | null>(null);
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState<"key" | "codes" | null>(null);
    const [remaining, setRemaining] = useState<number>(Math.max(0, Math.floor(challenge.expiresInSeconds)));
    const inputRef = useRef<HTMLInputElement>(null);
    const submittingRef = useRef(false);

    // The pending login lives five minutes; at zero the page sends the user back to the password.
    // Not while the backup codes are on screen: by then the session exists.
    useEffect(() => {
        if (screen === "backup-codes") return;
        if (remaining <= 0) { onExpired(); return; }
        const timer = window.setTimeout(() => setRemaining((r) => r - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [remaining, screen, onExpired]);

    const challengeFetch = useCallback((path: string, body?: unknown) => apiFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${challenge.challengeToken}` },
        body: body === undefined ? undefined : JSON.stringify(body),
    }), [challenge.challengeToken]);

    // Enrolment: fetch a fresh secret the moment the screen is shown. Nothing is stored server-side
    // until the first code is confirmed, so abandoning here locks nobody out.
    useEffect(() => {
        if (screen !== "enrol" || enrol) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await challengeFetch(`/login/2fa/enrol`);
                const body = await res.json().catch(() => ({} as any));
                if (cancelled) return;
                if (res.ok && typeof body?.secret === "string" && typeof body?.otpauth === "string") {
                    setEnrol({ secret: body.secret, otpauth: body.otpauth, label: body.label });
                    return;
                }
                if (res.status === 401) { onExpired(); return; }
                // Already enrolled (a concurrent setup, or the page's flag was stale): ask for the code.
                if (res.status === 409) { setScreen("verify"); return; }
                setEnrolError(body?.message ?? t("tfa_error_generic"));
            } catch {
                if (!cancelled) setEnrolError(t("tfa_error_generic"));
            }
        })();
        return () => { cancelled = true; };
    }, [screen, enrol, challengeFetch, onExpired, t]);

    const finish = (body: TwoFactorCompletion) => {
        if (typeof body.trustToken === "string") storeTrustToken(body.trustToken);
        onComplete(body);
    };

    const failureMessage = (status: number, body: any): string => {
        if (status === 423) {
            const minutes = Math.max(1, Math.ceil((typeof body?.retryAfterSeconds === "number" ? body.retryAfterSeconds : 900) / 60));
            return t("tfa_locked", { minutes: String(minutes) });
        }
        if (status === 429) return t("tfa_rate_limited");
        if (status === 401 || status === 400) return screen === "enrol" ? t("tfa_enrol_error_invalid_code") : t("tfa_error_invalid_code");
        return body?.message ?? t("tfa_error_generic");
    };

    const submit = async (value: string) => {
        if (submittingRef.current) return;
        const cleaned = value.trim();
        if (cleaned === "") { setError(t("tfa_error_required")); return; }
        if (!useBackup && !DIGITS.test(cleaned)) { setError(t("tfa_error_invalid_code")); return; }
        if (useBackup && !BACKUP.test(cleaned)) { setError(t("tfa_error_invalid_code")); return; }
        submittingRef.current = true;
        setSubmitting(true);
        setError(null);
        try {
            const enrolling = screen === "enrol";
            const res = await challengeFetch(
                enrolling ? `/login/2fa/enrol/confirm` : `/login/2fa/verify`,
                enrolling ? { secret: enrol?.secret, otp: cleaned, trustDevice } : { otp: cleaned, trustDevice },
            );
            const body = await res.json().catch(() => ({} as any));
            if (res.ok) {
                if (enrolling) {
                    // Hold the payload (and the trust token) until the codes are acknowledged.
                    setBackupCodes(Array.isArray(body?.backupCodes) ? body.backupCodes : []);
                    setFinalPayload(body as TwoFactorCompletion);
                    setScreen("backup-codes");
                    return;
                }
                finish(body as TwoFactorCompletion);
                return;
            }
            if (res.status === 401 && body?.code === "two_factor_challenge_expired") { onExpired(); return; }
            if (res.status === 409 && body?.code === "two_factor_enrolment_required") {
                // Reset by an admin between the password and the code: enrol instead.
                setEnrol(null);
                setScreen("enrol");
                setOtp("");
                return;
            }
            if (res.status === 409 && body?.code === "two_factor_already_enrolled") { setScreen("verify"); setOtp(""); return; }
            setError(failureMessage(res.status, body));
            setOtp("");
            inputRef.current?.focus();
        } catch {
            setError(t("tfa_error_generic"));
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const onCodeChange = (raw: string) => {
        const next = useBackup
            ? raw.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 9)
            : raw.replace(/\D/g, "").slice(0, 6);
        setOtp(next);
        setError(null);
        // Six digits typed (or pasted, or filled from an SMS-style suggestion): go.
        if (!useBackup && DIGITS.test(next)) void submit(next);
    };

    const copy = async (text: string, what: "key" | "codes") => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(what);
            window.setTimeout(() => setCopied((c) => (c === what ? null : c)), 2000);
        } catch { /* clipboard unavailable — the text is selectable */ }
    };

    const download = () => {
        const blob = new Blob([`${t("tfa_backup_codes_file_header")}\n\n${backupCodes.join("\n")}\n`], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "portal-backup-codes.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const minutes = Math.floor(remaining / 60);
    const seconds = String(remaining % 60).padStart(2, "0");

    const codeInput = (
        <div className="tfa-field">
            <label htmlFor="tfa-code" className="tfa-label">
                {useBackup ? t("tfa_backup_code_label") : t("tfa_code_label")}
            </label>
            <input
                id="tfa-code"
                ref={inputRef}
                className={`login-input tfa-input${useBackup ? "" : " tfa-input--digits"}`}
                type="text"
                name="otp"
                inputMode={useBackup ? "text" : "numeric"}
                pattern={useBackup ? undefined : "[0-9]*"}
                autoComplete="one-time-code"
                autoCapitalize={useBackup ? "characters" : "none"}
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                maxLength={useBackup ? 9 : 6}
                placeholder={useBackup ? t("tfa_backup_code_placeholder") : t("tfa_code_placeholder")}
                value={otp}
                onChange={(e) => onCodeChange(e.target.value)}
                disabled={submitting}
                autoFocus
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "tfa-error" : undefined}
            />
            {error && <p id="tfa-error" className="tfa-error" role="alert">{error}</p>}
        </div>
    );

    const trustBox = (
        <label className="tfa-trust">
            <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} disabled={submitting} />
            <span>{t("tfa_trust_device")}</span>
        </label>
    );

    const backButton = (
        <button type="button" className="tfa-back" onClick={onBack} disabled={submitting}>
            <ArrowLeft size={14} aria-hidden="true" /> {t("tfa_back_to_login")}
        </button>
    );

    if (screen === "backup-codes") {
        return (
            <div className="tfa" data-screen="backup-codes">
                <div className="tfa-head">
                    <ShieldCheck className="tfa-icon" size={28} aria-hidden="true" />
                    <div className="tfa-head-text">
                        <h2 className="tfa-title">{t("tfa_backup_codes_title")}</h2>
                        <p className="tfa-subtitle">{t("tfa_backup_codes_subtitle")}</p>
                    </div>
                </div>
                <ul className="tfa-codes" aria-label={t("tfa_backup_codes_title")}>
                    {backupCodes.map((c) => <li key={c}><code>{c}</code></li>)}
                </ul>
                <p className="tfa-warning">{t("tfa_backup_codes_warning")}</p>
                <div className="tfa-code-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(backupCodes.join("\n"), "codes")}>
                        {copied === "codes" ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                        {copied === "codes" ? t("tfa_backup_codes_copied") : t("tfa_backup_codes_copy")}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={download}>
                        <Download size={14} aria-hidden="true" /> {t("tfa_backup_codes_download")}
                    </button>
                </div>
                <label className="tfa-trust tfa-confirm-saved">
                    <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
                    <span>{t("tfa_backup_codes_confirm_checkbox")}</span>
                </label>
                <button
                    type="button"
                    className="btn btn-primary btn-full"
                    disabled={!saved || !finalPayload}
                    onClick={() => finalPayload && finish(finalPayload)}
                >
                    {t("tfa_backup_codes_continue")}
                </button>
            </div>
        );
    }

    if (screen === "enrol") {
        return (
            <form className="tfa" data-screen="enrol" onSubmit={(e) => { e.preventDefault(); void submit(otp); }}>
                <div className="tfa-head">
                    <Smartphone className="tfa-icon" size={28} aria-hidden="true" />
                    <div className="tfa-head-text">
                        <h2 className="tfa-title">{t("tfa_enrol_title")}</h2>
                        <p className="tfa-subtitle">{t("tfa_enrol_subtitle")}</p>
                    </div>
                </div>

                {/* Two steps: stacked on a phone, side by side on desktop (two-factor.css). */}
                <div className="tfa-enrol-grid">
                <section className="tfa-section">
                    <h3 className="tfa-step">{t("tfa_enrol_step1_title")}</h3>
                    <p className="tfa-help">{t("tfa_enrol_step1_body")}</p>
                    {enrol ? (
                        <>
                            <div className="tfa-qr" aria-label={t("tfa_enrol_qr_alt")}>
                                <QRCodeSVG value={enrol.otpauth} size={160} level="M" includeMargin={false} />
                            </div>
                            <p className="tfa-help tfa-help--tight">{t("tfa_enrol_manual_entry_label")}</p>
                            <div className="tfa-secret">
                                <code>{groupKey(enrol.secret)}</code>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(enrol.secret, "key")} aria-label={t("tfa_enrol_copy_secret")}>
                                    {copied === "key" ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                                    {copied === "key" ? t("tfa_enrol_secret_copied") : t("tfa_enrol_copy_secret")}
                                </button>
                            </div>
                        </>
                    ) : enrolError ? (
                        <p className="tfa-error" role="alert">{enrolError}</p>
                    ) : (
                        <p className="tfa-help" aria-busy="true">{t("tfa_enrol_loading")}</p>
                    )}
                </section>

                <section className="tfa-section">
                    <h3 className="tfa-step">{t("tfa_enrol_step2_title")}</h3>
                    <p className="tfa-help">{t("tfa_enrol_step2_body")}</p>
                    {/* The trust box sits ABOVE the code: six digits submit on their own,
                        so anything the user wants to change must come before typing. */}
                    {trustBox}
                    {codeInput}
                    {/* Inside step 2 so it fills the column on desktop and the card stays one screen tall. */}
                    <button type="submit" className="btn btn-primary btn-full tfa-confirm" disabled={submitting || !enrol || !DIGITS.test(otp)}>
                        {submitting ? t("tfa_enrol_confirming") : t("tfa_enrol_confirm_button")}
                    </button>
                </section>
                </div>

                <div className="tfa-foot">
                    {backButton}
                    <span className="tfa-countdown" aria-live="off">{t("tfa_time_left", { time: `${minutes}:${seconds}` })}</span>
                </div>
            </form>
        );
    }

    return (
        <form className="tfa" data-screen="verify" onSubmit={(e) => { e.preventDefault(); void submit(otp); }}>
            <div className="tfa-head">
                <KeyRound className="tfa-icon" size={28} aria-hidden="true" />
                <div className="tfa-head-text">
                    <h2 className="tfa-title">{t("tfa_verify_title")}</h2>
                    <p className="tfa-subtitle">{t("tfa_signed_in_as", { name: challenge.user.name })}</p>
                </div>
            </div>
            <p className="tfa-help">{useBackup ? t("tfa_backup_code_help") : t("tfa_verify_subtitle")}</p>
            {/* Trust box first: six digits submit on their own (see onCodeChange). */}
            {trustBox}
            {codeInput}
            <button type="submit" className="btn btn-primary btn-full" disabled={submitting || otp === ""}>
                {submitting ? t("tfa_verifying") : t("tfa_verify_button")}
            </button>
            <button
                type="button"
                className="tfa-link"
                onClick={() => { setUseBackup((b) => !b); setOtp(""); setError(null); window.setTimeout(() => inputRef.current?.focus(), 0); }}
                disabled={submitting}
            >
                {useBackup ? t("tfa_use_authenticator_code") : t("tfa_use_backup_code")}
            </button>
            <div className="tfa-foot">
                {backButton}
                <span className="tfa-countdown" aria-live="off">{t("tfa_time_left", { time: `${minutes}:${seconds}` })}</span>
            </div>
        </form>
    );
};

export default TwoFactorStep;
